import React, { Component } from 'react'

/**
 * BBCodeWidget.js
 *
 * A generic host-integratable widget that renders ANY standalone "vibecode"
 * HTML page (such as the UML React Flow page) and feeds it the SPARQL results
 * coming from the host application.
 *
 * How it works
 * ------------
 * The host discovers this widget through `static getDescription()` and renders
 * it, passing each declared variable as a prop. The SPARQL 1.1 JSON results of
 * the `query` variable arrive as `this.props.data`.
 *
 * The pasted page is a COMPLETE HTML document (with its own <head>, styles and
 * <script type="module"> ... </script>). Such a document cannot be injected
 * into the host DOM directly without clashing, so we load it into a sandboxed
 * <iframe> via `srcdoc`. Before the page's own scripts run we inject a tiny
 * bootstrap that publishes the host data on the page's window:
 *
 *     window.WIDGET_DATA    -> the SPARQL 1.1 results JSON (this.props.data)
 *     window.WIDGET_CONFIG  -> optional config object (this.props.config)
 *
 * When the host later changes the data (new query results) we postMessage the
 * iframe; the bootstrap updates window.WIDGET_DATA and fires a DOM event so the
 * page can re-render live:
 *
 *     window.addEventListener('widget:data',   e => { ... e.detail ... })
 *     window.addEventListener('widget:config', e => { ... e.detail ... })
 *
 * Contract for the embedded page
 * ------------------------------
 *   const results = window.WIDGET_DATA || <your sample data>;   // read on load
 *   window.addEventListener('widget:data', e => rerender(e.detail)); // live updates
 *
 * A page may talk back to the host by posting an "up" message; it is forwarded
 * to this.props.onChange:
 *
 *     parent.postMessage({ __widgetUp: true, type: 'change', payload }, '*')
 *
 * Variables (descriptor parameters)
 * ---------------------------------
 *   query  (yasgui)   — SPARQL SELECT. Its JSON results arrive as this.props.data.
 *   html   (textarea) — a complete standalone HTML page (e.g. the UML page).
 *   config (json)     — optional config object exposed as window.WIDGET_CONFIG.
 */

const PLACEHOLDER_HTML = `<!doctype html><html><head><meta charset="utf-8">
<style>html,body{height:100%;margin:0;font:14px/1.5 -apple-system,Segoe UI,Roboto,sans-serif;
color:#6b7383;background:#fff;display:flex;align-items:center;justify-content:center;text-align:center}
div{max-width:420px;padding:24px}</style></head>
<body><div><h3>No HTML page configured</h3>
<p>Paste a complete standalone HTML page into the <b>html</b> parameter.
It will run here and receive the SPARQL results as <code>window.WIDGET_DATA</code>.</p></div></body></html>`

export default class BBCodeWidget extends Component {
  constructor(props) {
    super(props)
    this.iframeRef = React.createRef()
    this.onMessage = this.onMessage.bind(this)
  }

  static getDescription() {
    return {
      name: 'BBCodeWidget',
      component: BBCodeWidget,
      label: 'HTML Page Widget',
      variables: [
        {
          name: 'query',
          type: 'yasgui',
          label:
            'SPARQL SELECT query. Its JSON results arrive as this.props.data and ' +
            'are injected into the HTML page as window.WIDGET_DATA.',
        },
        {
          name: 'html',
          type: 'textarea',
          label:
            'Paste a complete standalone HTML page (e.g. the UML React Flow page). ' +
            'It runs in a sandboxed iframe and reads the SPARQL results from ' +
            'window.WIDGET_DATA (and live updates via the "widget:data" event).',
        },
        {
          name: 'config',
          type: 'json',
          label:
            'Optional configuration object, exposed to the page as ' +
            'window.WIDGET_CONFIG, e.g. { "layout": "LR" }',
        },
      ],
    }
  }

  componentDidMount() {
    window.addEventListener('message', this.onMessage)
    this.writeDoc()
  }

  componentDidUpdate(prevProps) {
    // The page source changed -> rebuild the whole document.
    if (prevProps.html !== this.props.html) {
      this.writeDoc()
      return
    }
    // Data / config changed -> push them into the already-loaded page.
    if (prevProps.data !== this.props.data || prevProps.config !== this.props.config) {
      this.pushData()
    }
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.onMessage)
  }

  // Messages coming UP from the embedded page -> forward to host onChange.
  onMessage(ev) {
    const iframe = this.iframeRef.current
    if (!iframe || ev.source !== iframe.contentWindow) return
    const msg = ev.data
    if (!msg || msg.__widgetUp !== true) return
    if (msg.type === 'change' && typeof this.props.onChange === 'function') {
      this.props.onChange(msg.payload)
    }
  }

  // Safe JSON for inlining inside a <script> tag (prevents </script> breakout).
  serialize(value) {
    let json
    try {
      json = JSON.stringify(value === undefined ? null : value)
    } catch (e) {
      json = 'null'
    }
    return json.replace(/</g, '\\u003c').replace(/-->/g, '--\\u003e')
  }

  // The bootstrap runs (as a classic, synchronous <script>) before the page's
  // own deferred module scripts, so WIDGET_DATA is ready when they execute.
  bootstrapScript() {
    return (
      '<script>(function(){' +
      'try{window.WIDGET_DATA=' + this.serialize(this.props.data) + ';}catch(e){window.WIDGET_DATA=null;}' +
      'try{window.WIDGET_CONFIG=' + this.serialize(this.props.config) + ';}catch(e){window.WIDGET_CONFIG=null;}' +
      'window.addEventListener("message",function(ev){' +
      'var m=ev.data;if(!m||m.__widget!==true)return;' +
      'if(m.type==="data"){window.WIDGET_DATA=m.data;' +
      'window.dispatchEvent(new CustomEvent("widget:data",{detail:m.data}));}' +
      'if(m.type==="config"){window.WIDGET_CONFIG=m.config;' +
      'window.dispatchEvent(new CustomEvent("widget:config",{detail:m.config}));}' +
      '});' +
      '})();<\/script>'
    )
  }

  // Insert the bootstrap at the very start of <head> so it runs first.
  buildDoc() {
    const html = this.props.html && this.props.html.trim() ? this.props.html : PLACEHOLDER_HTML
    const boot = this.bootstrapScript()
    if (/<head[^>]*>/i.test(html)) {
      return html.replace(/<head[^>]*>/i, (m) => m + '\n' + boot)
    }
    if (/<html[^>]*>/i.test(html)) {
      return html.replace(/<html[^>]*>/i, (m) => m + '\n<head>' + boot + '</head>')
    }
    return boot + html
  }

  writeDoc() {
    const iframe = this.iframeRef.current
    if (!iframe) return
    iframe.srcdoc = this.buildDoc()
  }

  pushData() {
    const iframe = this.iframeRef.current
    if (!iframe || !iframe.contentWindow) return
    iframe.contentWindow.postMessage({ __widget: true, type: 'data', data: this.props.data }, '*')
    iframe.contentWindow.postMessage({ __widget: true, type: 'config', config: this.props.config }, '*')
  }

  render() {
    return (
      <iframe
        ref={this.iframeRef}
        title="BBCodeWidget"
        sandbox="allow-scripts allow-popups"
        style={{ width: '100%', height: '100%', minHeight: '640px', border: '0', display: 'block' }}
      />
    )
  }
}
